import { useEffect, useMemo, useRef, useState } from "react";
import { LoadingView } from "../../../../components";
import useGetSummarizedData from "./useGetSummarizedData";
import { useStyletron } from "baseui";
import { addMinutes, endOfDay, roundToNearestMinutes, startOfDay } from "date-fns";
import TimeNavigation, { onTimeChangedArgs } from "../../TimeNavigation/TimeNavigation";
import { defaultGrace, defaultOptions } from "./options";
import { Block } from "baseui/block";
import { useGetLanguage } from "../../../../hooks/useGetLanguage";
import { annotation } from "./annotation";
import { Bar } from "react-chartjs-2";
import { Button, KIND } from "baseui/button";
import { ResetZoomIcon } from "../../../../Icons/ResetZoomIcon";
import { TBarChartItem, TLineChartItem } from "../types";
import { ValueDisplayWithLabel } from "../../../../automationObjects/components/ValueDisplay";
import { FormattedMessage } from "react-intl";
import React from "react";
import { CSVDownload, CSVLink } from "react-csv";
import { PiFileCsvLight } from "react-icons/pi";
import useIsMobile from "../../../../hooks/useIsMobile/useIsMobile";

let timer;

const TimeSeriesLineChart = (props: {
    lines: TBarChartItem[],
    summarizeBy: string,
    timespan: number,
    showTimeNavigation?: boolean,
    showAverage?: boolean,
    showMinValue?: boolean,
    showMaxValue?: boolean,
    showLegend?: boolean,
    enableZoom?: boolean,
    endTime?: Date | undefined

}) => {

    const [css, theme] = useStyletron();
    const { locale } = useGetLanguage();
    const [state, setState] = useState({ start: getStart(props.timespan,props.endTime ?? new Date()), end: getEnd(props.endTime ?? new Date()), })
    const [live, setLive] = useState(true);
    const { items, isLoading, isSuccess, error } = useGetSummarizedData(props.lines, state.start, state.end, props.summarizeBy, false, theme.colors.contentInverseSecondary);
    const [isZoom, setIsZoom] = useState(false);
    const chartRef = useRef<any>(null);
    const [dataSets, setDataSets] = useState({ datasets: [] });
    const [max, setMax] = useState();
    const [min, setMin] = useState();
    const ismobile = useIsMobile();

    const mainLineDimension = props.lines[0].dimension;
    // update every x seconds
    useEffect(() => {
        const interval = setInterval(() => UpdateTimes(), 15 * 60 * 1000); // every 15 minutes
        return () => {
            clearInterval(interval);
        };
    });
    useEffect(() => {

            if (items) {

                setDataSets({ datasets: items?.flatMap((elm) => elm.dataSet) })
                const data = items[0].dataSet[0]?.data ?? items[0].dataSet?.data;
                if (props.showMaxValue) {
                    const maxObj = data?.reduce((accumulator, current) => {
                        return accumulator.y > current.y ? accumulator : current;
                    });
                    setMax(maxObj.y);
                }
                if (props.showMinValue) {
                    const minObj = data?.reduce((accumulator, current) => {
                        return accumulator.y < current.y ? accumulator : current;
                    });
                    setMin(minObj.y);
                }
            }
    }, [items]);

    const myOptions = useMemo(() => {
        const o = { ...defaultOptions }

        o.scales.y.ticks.color = theme.colors.contentTertiary;
        o.scales.x.ticks.color = theme.colors.contentTertiary;

        o.scales.y.grace = props.lines[0].grace ?? defaultGrace;
        o.scales.y1.grace = props.lines[0].grace ?? defaultGrace;
        o.scales.y3.grace = props.lines[0].grace ?? defaultGrace;
        o.plugins.zoom.zoom.wheel.enabled = props.enableZoom;
        o.plugins.zoom.zoom.pinch.enabled = props.enableZoom;
        o.plugins.zoom.zoom.drag.enabled = props.enableZoom;
        o.plugins.zoom.zoom.onZoomComplete = onZoomComplete;
        o.plugins.zoom.pan.enabled = props.enableZoom;
        o.plugins.zoom.pan.onPanComplete = onPanComplete;
        o.plugins.legend.display = props.showLegend ?? false;
        o.plugins.annotation.annotations.annotation = props.showAverage ? annotation : undefined;
        o.scales.x.adapters = { date: { locale: locale } };
        if (props.showAverage)
            o.plugins.annotation.annotations.annotation.borderColor = theme.colors.contentPrimary;
        if ( ismobile)
            o.scales.x.ticks.maxTicksLimit = 7;
        return o;
    }, [ locale]);

    const filename = props.lines[0].propertyName ? props.lines[0].propertyName + "_" + state.start.toDateString() + "_" + state.end.toDateString() + ".csv" : "LineChart_" + state.start.toDateString() + "_" + state.end.toDateString() + ".csv";

    const csvData = items && items.length > 0 ? items[0].dataSet[0]?.data ?? items[0].dataSet?.data : [];

    return (
        <LoadingView isLoading={isLoading} isSucess={isSuccess} error={error}>
            <Block height='100%'  display='flex' flexDirection='column' width={'100%'} gridGap={theme.sizing.scale800}>
                <Block display={'inline-flex'} alignSelf={'center'} gridGap={theme.sizing.scale400}>
                    {props.showMinValue && <ValueDisplayWithLabel mini dimension={mainLineDimension} value={min ?? 0} label={<FormattedMessage id="lowestValue" defaultMessage={'lowest'}></FormattedMessage>}></ValueDisplayWithLabel>}
                    {props.showMaxValue && <ValueDisplayWithLabel mini dimension={mainLineDimension} value={max ?? 0} label={<FormattedMessage id="highestValue" defaultMessage={'highest'}></FormattedMessage>}></ValueDisplayWithLabel>}
                </Block>
                <Block flex={1} >
                    <div className={(css({ position: 'relative',height: '100%' }))}>
                        {isZoom && <Button title='Reset Zoom' size='compact' shape='round' kind='secondary' className={(css({ position: 'absolute', right: 0, top: theme.sizing.scale800 }))} onClick={resetZoom}>
                            <ResetZoomIcon color={theme.colors.primary} />
                        </Button>}
                        <Bar ref={chartRef} data={dataSets} datasetIdKey='id' options={myOptions}></Bar>
                    </div>
                </Block>
                {props.showTimeNavigation &&
                    <Block display={'inline-flex'} gridGap={'15px'} >
                        <TimeNavigation start={state.start} end={state.end} goBack={goBack} goForward={goForward} goLive={goLive} onTimeChanged={onTimeChanged}></TimeNavigation>
                        <CSVLink data={csvData} filename={filename} target="_blank" ><Button size='compact' kind={KIND.secondary} > <PiFileCsvLight></PiFileCsvLight> </Button></CSVLink>
                    </Block>
                }
            </Block>
        </LoadingView>
    );

    function fetch(start: Date, end: Date) {
        setLive(false);
        if (start > state.start && end < state.end)
            return;
        setState({
            start: start,
            end: end,
        })
    }

    function resetZoom() {
        chartRef?.current?.resetZoom(); setIsZoom(false);
    }
    function onZoomComplete({ chart }) {
        const zoomed = chart.getZoomLevel() !== 1;
        if (!zoomed)
            return;
        startFetch({ chart });
        setIsZoom(zoomed);

    }
    function onPanComplete({ chart }) {
        startFetch({ chart });
    }
    function startFetch({ chart }) {
        console.log("startFetch");
        const { min, max } = chart.scales.x;
        clearTimeout(timer);
        timer = setTimeout(() => {
            //console.log('Fetched data between ' + new Date(min) + ' and ' + new Date(max));
            fetch(new Date(min), new Date(max));
            //chart.data.datasets[0].data = fetchData(min, max);
            chart.stop(); // make sure animations are not running
            chart.update('none');
        }, 200);
    }

    function UpdateTimes() {
        if (!live)
            return;
        console.log("UpdateTimes")
        setState({ start: getStart(props.timespan,new Date()), end: getEnd(new Date()), })
    }
    function goBack() {
        console.log("goBack")
        setLive(false);
        setState({
            start: addMinutes(state.start, -(props.timespan - 1440)),
            end: addMinutes(state.end, -(props.timespan - 1440)),
        })
        resetZoom();
    }
    function goForward() {
        console.log("goForward")
        setLive(false);
        setState({
            start: addMinutes(state.start, (props.timespan - 1440)),
            end: addMinutes(state.end, (props.timespan - 1440)),
        })
        resetZoom();
    }
    function onTimeChanged(item?: onTimeChangedArgs) {
        if (item) {
            console.log("onTimeChanged", item)
            setLive(false);
            setState({
                start: item.start,
                end: item.end,
            })
            resetZoom();
        }

    }
    function goLive() {
        console.log("goLive")
        setLive(true);
        setState({ start: getStart(props.timespan,new Date()), end: getEnd(new Date()), })
        resetZoom();
    }
}

export default React.memo(TimeSeriesLineChart);


function getStart(timespan: number, date: Date): Date {
    return startOfDay(addMinutes(startOfDay(date), -(timespan)))
}
function getEnd(date: Date): Date {
    return roundToNearestMinutes(date, { nearestTo: 5 })
}

